import * as React from 'react';
import { cn } from '@/lib/utils';

export interface PhoneInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  onChange?: (value: string) => void;
  value?: string;
}

const PhoneInput = React.forwardRef<HTMLInputElement, PhoneInputProps>(
  ({ className, onChange, value, ...props }, ref) => {
    const [internalValue, setInternalValue] = React.useState(value || '');

    // Format phone number for display
    const formatPhoneNumber = (input: string) => {
      // Remove all non-digits
      const digits = input.replace(/\D/g, '');

      // Limit to 15 digits (max international phone number length)
      const trimmed = digits.slice(0, 15);

      // Format based on length (assuming US format for now)
      if (trimmed.length === 0) return '';
      if (trimmed.length <= 3) return trimmed;
      if (trimmed.length <= 6) return `(${trimmed.slice(0, 3)}) ${trimmed.slice(3)}`;
      if (trimmed.length <= 10)
        return `(${trimmed.slice(0, 3)}) ${trimmed.slice(3, 6)}-${trimmed.slice(6)}`;

      // For numbers longer than 10 digits, assume international
      // Format as +X (XXX) XXX-XXXX or +XX XXXXX...
      const countryCode = trimmed.slice(0, trimmed.length - 10);
      const areaCode = trimmed.slice(-10, -7);
      const prefix = trimmed.slice(-7, -4);
      const lineNumber = trimmed.slice(-4);

      if (countryCode.length === 1) {
        // US/Canada format: +1 (XXX) XXX-XXXX
        return `+${countryCode} (${areaCode}) ${prefix}-${lineNumber}`;
      } else {
        // Other international: +XX XXXXX...
        const localNumber = trimmed.slice(countryCode.length);
        return `+${countryCode} ${localNumber}`;
      }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target.value;

      // Extract only digits from input
      const digits = input.replace(/\D/g, '');

      // Update internal value
      setInternalValue(input);

      // Call parent onChange with digits only
      if (onChange) {
        // Prepend + for international format if digits exist
        onChange(digits ? `+${digits}` : '');
      }
    };

    // Sync with external value changes
    React.useEffect(() => {
      if (value !== undefined) {
        setInternalValue(formatPhoneNumber(value.replace(/^\+/, '')));
      }
    }, [value]);

    return (
      <input
        ref={ref}
        type="tel"
        value={formatPhoneNumber(internalValue.replace(/\D/g, ''))}
        onChange={handleChange}
        placeholder="+1 (555) 123-4567"
        className={cn(
          'flex h-10 w-full rounded-md border border-coal/10 bg-white px-3 py-2 text-sm text-coal shadow-input placeholder:text-stone/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clay/40 focus-visible:border-clay/40 focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-150',
          className,
        )}
        {...props}
      />
    );
  },
);
PhoneInput.displayName = 'PhoneInput';

export { PhoneInput };
